import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import {
  getTeacherGuidanceResources,
  sendTeacherGuidanceResourceLink
} from "../services/APIClient";
import "./TeacherRoundThreeModal.css";

const RESOURCE_ACTIONS = {
  preview: "Open resource now",
  sendLink: "Send link to my email"
};

const FAQ_SEARCHES = [
  {
    label: "Junior Infants End of Year Screener Walk Through",
    query: "Junior Infants End of Year Screener Walk Through"
  },
  {
    label: "Senior Infants End of Year Screener Walk Through",
    query: "Senior Infants End of Year Screener Walk Through"
  },
  {
    label: "1st Class Baseline Screener Walk Through",
    query: "1st Class Baseline Screener Walk Through"
  }
];

const GRADE_RESOURCE_ORDER = [
  {
    order: 0,
    fields: ["grade_level1", "gradeLevel1"],
    aliases: ["junior infants", "junior infant", "ji"]
  },
  {
    order: 1,
    fields: ["grade_level2", "gradeLevel2"],
    aliases: ["senior infants", "senior infant", "si"]
  },
  {
    order: 2,
    fields: ["grade_level3", "gradeLevel3"],
    aliases: ["1st class", "first class", "year 2", "year two"]
  }
];

const COUNTRY_REFERENCES = [
  {
    id: "07d45b79-24b1-435f-b82a-93861b23a86e",
    name: "Republic of Ireland",
    code: "01",
    aliases: ["Ireland", "Irish", "ROI"]
  },
  {
    id: "622e7844-a15e-4b91-9f64-12bf71ed7cff",
    name: "Northern Ireland",
    code: "02",
    aliases: ["NI", "Northern Irish"]
  },
  {
    id: "74bb2897-dbbe-41e3-b547-caee866545cf",
    name: "England",
    code: "03",
    aliases: ["English"]
  },
  {
    id: "5a2136da-62fa-4193-803e-33fd9be5dd87",
    name: "Scotland",
    code: "04",
    aliases: ["Scottish"]
  },
  {
    id: "5794e501-b06a-4963-b8b3-87665cc073ae",
    name: "Wales",
    code: "05",
    aliases: ["Welsh"]
  },
  {
    id: "ab40949f-001e-48b9-848e-126decad8471",
    name: "USA",
    code: "06",
    aliases: ["United States", "United States of America", "US"]
  },
  {
    id: "1cb16a5c-7c0a-4e7a-bcbc-b409024dd6cf",
    name: "United Arab Emirates",
    code: "07",
    aliases: ["UAE"]
  },
  {
    id: "f2d8569e-47ca-4e88-8736-797a7acd37f3",
    name: "Malaysia",
    code: "08",
    aliases: ["Malaysian"]
  },
  {
    id: "cd512001-3e26-4725-9a17-1155fc722db7",
    name: "India",
    code: "09",
    aliases: ["Indian"]
  }
];

const normaliseText = (value) => String(value || "").trim().toLowerCase();

const displayText = (value, fallback = "") => String(value || fallback).trim();

const hasPositiveValue = (value) => {
  if (value === true || value === 1) {
    return true;
  }

  return ["true", "1", "yes"].includes(normaliseText(value));
};

const hasUnavailableValue = (value) => {
  if (value === false || value === 0) {
    return true;
  }

  return ["false", "0", "no", "unavailable", "inactive", "disabled"].includes(normaliseText(value));
};

const normaliseIdList = (value) => {
  if (Array.isArray(value)) {
    return value.flatMap(normaliseIdList);
  }

  if (value && typeof value === "object") {
    return normaliseIdList(
      value.id ||
        value._id ||
        value.countryId ||
        value.countryid ||
        value.country_id ||
        value.countryCode ||
        value.countrycode ||
        value.country_code ||
        value.value ||
        value.name ||
        value.countryName ||
        value.country_name ||
        value.code
    );
  }

  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const getResourceCountryIds = (resource) => [
  ...normaliseIdList(resource?.country),
  ...normaliseIdList(resource?.countryName),
  ...normaliseIdList(resource?.country_name),
  ...normaliseIdList(resource?.countryCode),
  ...normaliseIdList(resource?.countrycode),
  ...normaliseIdList(resource?.country_code),
  ...normaliseIdList(resource?.countryId),
  ...normaliseIdList(resource?.countryid),
  ...normaliseIdList(resource?.country_id),
  ...normaliseIdList(resource?.countryIds),
  ...normaliseIdList(resource?.countryids),
  ...normaliseIdList(resource?.country_ids),
  ...normaliseIdList(resource?.countries)
];

const getCountryReferenceValues = (value) => {
  const rawValue = normaliseText(value);

  if (!rawValue) {
    return [];
  }

  const matchingCountry = COUNTRY_REFERENCES.find((country) =>
    [country.id, country.code, country.name, ...(country.aliases || [])]
      .map(normaliseText)
      .includes(rawValue)
  );

  if (!matchingCountry) {
    return [rawValue];
  }

  return [matchingCountry.id, matchingCountry.code, matchingCountry.name, ...(matchingCountry.aliases || [])]
    .map(normaliseText)
    .filter(Boolean);
};

const containsAlias = (text, alias) => {
  const comparableText = normaliseText(text).replace(/\s+/g, " ");
  const comparableAlias = normaliseText(alias).replace(/\s+/g, " ");

  if (!comparableText || !comparableAlias) {
    return false;
  }

  if (comparableAlias.length <= 2) {
    return new RegExp(`(^|[^a-z0-9])${comparableAlias}([^a-z0-9]|$)`).test(comparableText);
  }

  return comparableText.includes(comparableAlias);
};

const isIframeResource = (resource) => normaliseText(resource?.type) === "iframe";

const isPdfResource = (resource) => {
  const type = normaliseText(resource?.type);
  const url = normaliseText(resource?.url);

  return type.includes("pdf") || url.includes(".pdf");
};

const canPreviewResource = (resource) => isIframeResource(resource) || isPdfResource(resource);

const normaliseKeywords = (keywords) => {
  if (Array.isArray(keywords)) {
    return keywords;
  }

  return String(keywords || "")
    .split(",")
    .map((keyword) => keyword.trim())
    .filter(Boolean);
};

const getResourceGradeSortOrder = (resource, normalisedResource) => {
  const searchableText = [
    normalisedResource.title,
    normalisedResource.description,
    normalisedResource.keywords.join(" "),
    resource?.grade,
    resource?.gradeName,
    resource?.grade_name,
    resource?.classLevel,
    resource?.class_level
  ].join(" ");

  const matchingGrade = GRADE_RESOURCE_ORDER.find((grade) =>
    grade.fields.some((field) => hasPositiveValue(resource?.[field])) ||
    grade.aliases.some((alias) => containsAlias(searchableText, alias))
  );

  return matchingGrade?.order ?? GRADE_RESOURCE_ORDER.length;
};

const isResourceExplicitlyUnavailable = (resource) =>
  [
    resource?.active,
    resource?.enabled,
    resource?.available,
    resource?.isActive,
    resource?.is_active,
    resource?.isAvailable,
    resource?.is_available
  ].some(hasUnavailableValue) ||
  ["unavailable", "inactive", "disabled"].includes(normaliseText(resource?.status));

const normaliseResource = (resource, sortIndex = 0) => {
  const countryIds = getResourceCountryIds(resource);
  const normalisedResource = {
    id: resource?.id || resource?._id || resource?.resourceId || resource?.resource_id,
    title: displayText(resource?.name || resource?.title, "Untitled resource"),
    description: displayText(resource?.description),
    keywords: normaliseKeywords(resource?.keywords),
    type: displayText(resource?.type, "link"),
    url: displayText(resource?.url),
    countryId: countryIds[0] || "",
    countryIds,
    related: displayText(resource?.relatedResourceTitle || resource?.related, "None"),
    sortIndex,
    isUnavailable: isResourceExplicitlyUnavailable(resource)
  };

  return {
    ...normalisedResource,
    gradeSortOrder: getResourceGradeSortOrder(resource, normalisedResource)
  };
};

const compareGuidanceResources = (firstResource, secondResource) => {
  const gradeDifference = firstResource.gradeSortOrder - secondResource.gradeSortOrder;

  return gradeDifference || firstResource.sortIndex - secondResource.sortIndex;
};

const isResourceAvailableForCountry = (resource, countryId) => {
  if (resource.isUnavailable) {
    return false;
  }

  if (!countryId) {
    return true;
  }

  if (resource.countryIds.length < 1) {
    return false;
  }

  const activeCountryValues = getCountryReferenceValues(countryId);

  return resource.countryIds.some((resourceCountryId) =>
    getCountryReferenceValues(resourceCountryId).some((resourceCountryValue) =>
      activeCountryValues.includes(resourceCountryValue)
    )
  );
};

const normaliseResourceAction = (action) => {
  const value = displayText(action).toLowerCase();

  if (value.includes("send")) {
    return RESOURCE_ACTIONS.sendLink;
  }

  return RESOURCE_ACTIONS.preview;
};

const getDisplayResourceType = (resource) => {
  const type = normaliseText(resource?.type);
  const url = normaliseText(resource?.url);

  if (isPdfResource(resource)) {
    return "PDF";
  }

  if (isIframeResource(resource)) {
    return "YouTube video";
  }

  if (type.includes("video") || url.includes("youtube.com") || url.includes("youtu.be")) {
    return "Video link";
  }

  return "Resource link";
};

const getPreviewUrl = (url) => {
  const trimmedUrl = displayText(url);

  if (!trimmedUrl) {
    return "";
  }

  try {
    const parsedUrl = new URL(trimmedUrl);

    if (parsedUrl.hostname.includes("youtu.be")) {
      const videoId = parsedUrl.pathname.replace("/", "");
      return videoId ? `https://www.youtube.com/embed/${videoId}` : trimmedUrl;
    }

    if (parsedUrl.hostname.includes("youtube.com")) {
      const videoId = parsedUrl.searchParams.get("v");
      return videoId ? `https://www.youtube.com/embed/${videoId}` : trimmedUrl;
    }
  } catch (error) {
    return trimmedUrl;
  }

  return trimmedUrl;
};

const getResourceReference = (response) => response?.reference || response?.data?.reference || "";

const getResourceSendStatus = (response) => response?.status || response?.data?.status || "";

function TeacherScreeningGuidanceResourcesModal({
  show,
  handleClose,
  prefill = null,
  countryId = "",
  teacherEmail = "",
  onReturnToTeacherDashboard = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("form");
  const [guidanceQuery, setGuidanceQuery] = useState("");
  const [submittedGuidanceQuery, setSubmittedGuidanceQuery] = useState("");
  const [searchSequence, setSearchSequence] = useState(0);
  const [resources, setResources] = useState([]);
  const [selectedResource, setSelectedResource] = useState(null);
  const [isResourceListExpanded, setIsResourceListExpanded] = useState(true);
  const [resourceAction, setResourceAction] = useState(RESOURCE_ACTIONS.preview);
  const [error, setError] = useState("");
  const [isLoadingResources, setIsLoadingResources] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resourceResult, setResourceResult] = useState(null);
  const activeCountryId = normaliseIdList(prefill?.countryId || prefill?.countryid || countryId)[0] || "";

  useEffect(() => {
    if (!show) return;
    const initialQuery = prefill?.guidanceQuery ||
      prefill?.guidance_query ||
      prefill?.resourceTitle ||
      prefill?.resource_title ||
      prefill?.resourceName ||
      prefill?.resource_name ||
      "";
    setStage("form");
    setGuidanceQuery(initialQuery);
    setSubmittedGuidanceQuery(initialQuery);
    setSearchSequence(0);
    setResources([]);
    setSelectedResource(null);
    setIsResourceListExpanded(true);
    setResourceAction(normaliseResourceAction(prefill?.resourceAction || prefill?.resource_action));
    setError("");
    setIsLoadingResources(false);
    setIsSubmitting(false);
    setResourceResult(null);
  }, [show, prefill]);

  useEffect(() => {
    if (selectedResource && !canPreviewResource(selectedResource) && resourceAction === RESOURCE_ACTIONS.preview) {
      setResourceAction(RESOURCE_ACTIONS.sendLink);
    }
  }, [selectedResource, resourceAction]);

  useEffect(() => {
    if (!show) return;

    const resourceTitle = prefill?.resourceTitle ||
      prefill?.resource_title ||
      prefill?.resourceName ||
      prefill?.resource_name;
    const searchQuery = submittedGuidanceQuery || resourceTitle || "";

    if (!normaliseText(searchQuery)) {
      setResources([]);
      setSelectedResource(null);
      setIsResourceListExpanded(true);
      setIsLoadingResources(false);
      setError("");
      return;
    }

    let cancelled = false;
    setIsLoadingResources(true);
    setError("");

    getTeacherGuidanceResources({
      query: searchQuery,
      type: prefill?.resourceType || prefill?.resource_type,
      countryId: prefill?.countryId || prefill?.countryid,
      limit: 12
    })
      .then((response) => {
        if (cancelled) return;

        const rows = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
        const nextResources = rows
          .map(normaliseResource)
          .filter((resource) => resource.id)
          .sort(compareGuidanceResources);
        const prefilledResource = nextResources.find((resource) =>
          normaliseText(resource.title) === normaliseText(
            prefill?.resourceTitle ||
              prefill?.resource_title ||
              prefill?.resourceName ||
              prefill?.resource_name
          ) ||
          normaliseText(resource.type) === normaliseText(prefill?.resourceType || prefill?.resource_type)
        );

        setResources(nextResources);
        setSelectedResource((currentResource) => {
          if (currentResource && nextResources.some((resource) => resource.id === currentResource.id)) {
            return nextResources.find((resource) => resource.id === currentResource.id);
          }

          return prefilledResource || null;
        });
        if (prefilledResource) {
          setIsResourceListExpanded(false);
        }
      })
      .catch((loadError) => {
        if (!cancelled) {
          console.error("Error loading teacher guidance resources:", loadError);
          setError("Guidance resources could not be loaded. Please try again.");
          setResources([]);
          setSelectedResource(null);
          setIsResourceListExpanded(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingResources(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [show, submittedGuidanceQuery, searchSequence, prefill]);

  const visibleResources = useMemo(() => {
    if (!normaliseText(submittedGuidanceQuery)) {
      return [];
    }

    return resources.filter((resource) => isResourceAvailableForCountry(resource, activeCountryId));
  }, [submittedGuidanceQuery, resources, activeCountryId]);

  useEffect(() => {
    if (selectedResource && !visibleResources.some((resource) => resource.id === selectedResource.id)) {
      setSelectedResource(null);
      setIsResourceListExpanded(true);
    }
  }, [selectedResource, visibleResources]);

  const selectResource = (resource) => {
    setSelectedResource(resource);
    setIsResourceListExpanded(false);
    setError("");
  };

  const clearSelectedResource = () => {
    setSelectedResource(null);
    setIsResourceListExpanded(true);
    setResourceAction(RESOURCE_ACTIONS.preview);
    setError("");
  };

  const resetForAnotherSearch = () => {
    setStage("form");
    setGuidanceQuery("");
    setSubmittedGuidanceQuery("");
    setSearchSequence(0);
    setSelectedResource(null);
    setIsResourceListExpanded(true);
    setResourceAction(RESOURCE_ACTIONS.preview);
    setError("");
    setIsSubmitting(false);
    setResourceResult(null);
  };

  const submitGuidanceSearch = (query = guidanceQuery, displayQuery = undefined) => {
    const nextQuery = displayText(query);
    const nextDisplay = displayText(displayQuery || query);

    setGuidanceQuery(nextDisplay);
    setSubmittedGuidanceQuery(nextQuery);
    setSearchSequence((currentSequence) => currentSequence + 1);
    setSelectedResource(null);
    setIsResourceListExpanded(true);
    setResources([]);
    setError("");
  };

  const handleResourceAction = async () => {
    if (!selectedResource) {
      setError("Choose a resource first.");
      return;
    }

    if (resourceAction === RESOURCE_ACTIONS.preview) {
      if (!canPreviewResource(selectedResource)) {
        setError("This resource can be sent by email, but is not available for in-app viewing.");
        return;
      }

      if (!selectedResource.url) {
        setError("This resource does not have an openable link yet.");
        return;
      }

      setResourceResult({
        status: "preview",
        resourceTitle: selectedResource.title,
        resourceUrl: selectedResource.url,
        previewUrl: getPreviewUrl(selectedResource.url)
      });
      setStage("preview");
      return;
    }

    setError("");
    setIsSubmitting(true);

    try {
      const response = await sendTeacherGuidanceResourceLink(selectedResource.id);
      setResourceResult({
        ...response,
        status: getResourceSendStatus(response),
        reference: getResourceReference(response),
        resourceTitle: response?.resourceTitle || selectedResource.title,
        resourceId: selectedResource.id,
        email: teacherEmail
      });
      setStage("success");
    } catch (submitError) {
      console.error("Error sending guidance resource link:", submitError);
      setError("The resource link could not be sent. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderResourceRows = () => {
    if (!normaliseText(submittedGuidanceQuery)) {
      return (
        <p className="teacher-round-three-intro">
          Search for a help topic, or choose a common question above.
        </p>
      );
    }

    if (isLoadingResources) {
      return (
        <div className="teacher-round-three-search-state">
          <Spinner animation="border" size="sm" />
          <span>Loading guidance resources...</span>
        </div>
      );
    }

    if (visibleResources.length < 1) {
      return <p className="teacher-round-three-intro">No matching guidance resources found. Try a different question.</p>;
    }

    if (selectedResource && !isResourceListExpanded) {
      return (
        <label className="teacher-round-three-option selected">
          <Form.Check type="checkbox" checked={true} onChange={clearSelectedResource} aria-label={selectedResource.title} />
          <span>
            <strong>{selectedResource.title}</strong>
            <span>{selectedResource.description || `${selectedResource.type} resource`}</span>
          </span>
        </label>
      );
    }

    return visibleResources.map((resource) => (
      <label key={resource.id} className={`teacher-round-three-option${selectedResource?.id === resource.id ? " selected" : ""}`}>
        <Form.Check type="radio" name="guidanceResource" checked={selectedResource?.id === resource.id} onChange={() => selectResource(resource)} aria-label={resource.title} />
        <span>
          <strong>{resource.title}</strong>
          <span>{resource.description || `${resource.type} resource`}</span>
        </span>
      </label>
    ));
  };

  const renderSuccessCopy = () => {
    if (resourceResult?.status === "sent") {
      return {
        title: "Link Sent",
        intro: `The ${selectedResource?.title?.toLowerCase() || "resource"} link was sent${teacherEmail ? ` to ${teacherEmail}` : " to your email"}.`,
        strong: "The resource link has been sent.",
        detail: teacherEmail ? `Sent to ${teacherEmail}.` : "Check your email for the link."
      };
    }

    return {
      title: "Link Not Sent",
      intro: `The ${selectedResource?.title?.toLowerCase() || "resource"} link could not be sent.`,
      strong: "The resource link was not sent.",
      detail: "Please try again."
    };
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: resourceResult?.reference || "",
        message: "Guidance workflow complete."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "preview" || stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const returnToTeacherDashboard = () => {
    if (embedded) {
      handleDone();
      return;
    }

    if (onReturnToTeacherDashboard) {
      onReturnToTeacherDashboard();
      return;
    }

    handleClose();
  };

  return (
    <Modal show={show} onHide={handleModalHide} centered backdrop="static" className="teacher-round-three-modal">
      {stage === "form" && (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">Search Help And Guidance</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="teacher-round-three-intro">Type the question you need help with.</p>
            {error && <Alert variant="danger">{error}</Alert>}
            <Form.Group className="teacher-round-three-section" controlId="guidanceSearch">
              <Form.Label>What do you need help with?</Form.Label>
              <Form.Text as="div" className="teacher-round-three-field-label">Search guidance</Form.Text>
              <Form.Control
                value={guidanceQuery}
                onChange={(event) => setGuidanceQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    submitGuidanceSearch();
                  }
                }}
                aria-label="Search guidance"
              />
              <Button
                className="teacher-round-three-search-button"
                variant="outline-primary"
                onClick={() => submitGuidanceSearch()}
                disabled={!normaliseText(guidanceQuery)}
              >
                Search
              </Button>
              <Form.Text className="teacher-round-three-help">Try one of the FAQs below if you are not sure what to search for.</Form.Text>
            </Form.Group>
            <div className="teacher-guidance-faqs" aria-label="Frequently asked questions">
              {FAQ_SEARCHES.map((faq) => (
                <Button
                  key={faq.label}
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => submitGuidanceSearch(faq.query, faq.label)}
                >
                  {faq.label}
                </Button>
              ))}
            </div>
            <Form.Group>
              <Form.Label>Recommended results</Form.Label>
              <div className="teacher-round-three-options">
                {renderResourceRows()}
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <span className="teacher-round-three-footer-note">{selectedResource ? "Resource selected." : "Choose a recommended resource."}</span>
            <Button variant="outline-secondary" onClick={handleClose}>Cancel</Button>
            <Button variant="primary" onClick={() => setStage("options")} disabled={!selectedResource}>Open resource</Button>
          </Modal.Footer>
        </>
      )}
      {stage === "options" && selectedResource && (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">Resource Options</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="teacher-round-three-intro">Choose how you want to handle this resource.</p>
            {error && <Alert variant="danger">{error}</Alert>}
            <div className="teacher-round-three-summary" aria-label="Selected resource">
              <span className="teacher-round-three-summary-row"><strong>Resource</strong><span>{selectedResource.title}</span></span>
              <span className="teacher-round-three-summary-row"><strong>Type</strong><span>{getDisplayResourceType(selectedResource)}</span></span>
              <span className="teacher-round-three-summary-row"><strong>Also recommended</strong><span>{selectedResource.related}</span></span>
            </div>
            <Form.Group>
              <Form.Label>Choose an option</Form.Label>
              <div className="teacher-round-three-options">
                {Object.values(RESOURCE_ACTIONS)
                  .filter((action) => action !== RESOURCE_ACTIONS.preview || canPreviewResource(selectedResource))
                  .map((action) => (
                  <label key={action} className={`teacher-round-three-option${resourceAction === action ? " selected" : ""}`}>
                    <Form.Check type="radio" name="resourceAction" checked={resourceAction === action} onChange={() => setResourceAction(action)} aria-label={action} />
                    <span>
                      <strong>{action}</strong>
                      <span>
                        {action === RESOURCE_ACTIONS.preview
                          ? `View the ${getDisplayResourceType(selectedResource).toLowerCase()} here.`
                          : "Email yourself the resource link."}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <span className="teacher-round-three-footer-note">Resource option ready.</span>
            <Button variant="outline-secondary" onClick={() => setStage("form")}>Back</Button>
            <Button variant="primary" onClick={handleResourceAction} disabled={isSubmitting}>
              {isSubmitting
                ? "Sending..."
                : resourceAction === RESOURCE_ACTIONS.preview
                  ? "Open resource"
                  : resourceAction}
            </Button>
          </Modal.Footer>
        </>
      )}
      {stage === "preview" && selectedResource && resourceResult && (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">{selectedResource.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="teacher-round-three-intro">
              Viewing {getDisplayResourceType(selectedResource).toLowerCase()}.
            </p>
            <div className="teacher-round-three-preview">
              <iframe
                title={selectedResource.title}
                src={resourceResult.previewUrl}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <span className="teacher-round-three-footer-note">Resource opened.</span>
            <Button variant="outline-secondary" onClick={() => setStage("options")}>Back</Button>
            {!embedded && (
              <Button variant="outline-secondary" onClick={returnToTeacherDashboard}>Return to Teacher Dashboard</Button>
            )}
            <Button variant="primary" onClick={handleDone}>Close</Button>
          </Modal.Footer>
        </>
      )}
      {stage === "success" && selectedResource && (
        <>
          {(() => {
            const copy = renderSuccessCopy();

            return (
              <>
                <Modal.Header closeButton>
                  <Modal.Title as="h2">{copy.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <p className="teacher-round-three-intro">{copy.intro}</p>
                  <Alert
                    variant={resourceResult?.status === "sent" ? "success" : "danger"}
                    className="teacher-round-three-success"
                  >
                    <span className="teacher-round-three-ok" aria-hidden="true">OK</span>
                    <span>
                      <strong>{copy.strong}</strong>
                      <span>{copy.detail}</span>
                    </span>
                  </Alert>
                  <div className="teacher-round-three-next">
                    <strong>What next?</strong>
                    <span>Search help again for another resource, or {embedded ? "return to SENSAI" : "return to Teacher Dashboard"}.</span>
                  </div>
                </Modal.Body>
              </>
            );
          })()}
          <Modal.Footer>
            <span className="teacher-round-three-footer-note">Task complete.</span>
            <Button variant="outline-secondary" onClick={resetForAnotherSearch}>Search help again</Button>
            {!embedded && (
              <Button variant="outline-secondary" onClick={returnToTeacherDashboard}>Return to Teacher Dashboard</Button>
            )}
            <Button variant="primary" onClick={handleDone}>Close</Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
}

export default TeacherScreeningGuidanceResourcesModal;
